/**
 * Amorcage.
 *
 * Enchainement: on recupere l'identite locale, on charge le monde et la
 * sauvegarde du joueur, on passe par le createur de bonhomme si c'est une
 * premiere partie, puis on lance la boucle de jeu.
 */
import { Jeu, type EtatSauve } from './jeu.ts'
import { Creation } from './ui/creation.ts'
import { Connexion } from './ui/connexion.ts'
import { apparenceAleatoire, apparenceParDefaut, validerApparence } from '../shared/data/apparence.ts'
import { besoinsNeufs } from '../shared/sim/besoins.ts'
import { Alea } from '../shared/sim/alea.ts'
import { TEMPS_DEPART } from '../shared/sim/temps.ts'
import { WORLD_SEED, WORLD_SIZE } from '../shared/constants.ts'
import type { AnimalSauve, Batiment, JardinCueilli, Vehicule } from '../shared/types/monde.ts'
import { arreterJournal, demarrerJournal, journal } from './log/journal.ts'
import { ecrireLocal, effacerLocal, lireLocal } from './stockage.ts'
import { audio } from './audio/audio.ts'

const CLE_TOKEN = 'jeu.token'
const CLE_NOM = 'jeu.nom'
/** Nom du compte connecte. Absent tant qu'on ne s'est pas connecte. */
const CLE_COMPTE = 'jeu.compte'
/** Pose une fois qu'on a choisi: evite de redemander a chaque chargement. */
const CLE_CHOIX = 'jeu.choixFait'
/**
 * PASSERELLE DES TESTS: joue en invite, sans compte ni salon.
 *
 * Le mode invite n'existe plus pour un joueur — l'ecran de connexion n'a plus
 * de bouton, et le serveur refuse un jeton sans compte partout hors du mode
 * test (`JEU_TEST=1`). Les tests de bout en bout, eux, en ont besoin: c'est le
 * seul mode ou l'on peut teleporter le bonhomme sans que l'autorite serveur le
 * recale (voir CLAUDE.md). Ils posent cette clef puis rechargent la page
 * (`entrerEnInvite` dans tests/e2e/aides.ts). La poser a la main contre un
 * serveur de production ne donne qu'un monde muet: toutes les routes de jeu y
 * repondent 401.
 */
const CLE_INVITE = 'jeu.invite'

const toile = document.getElementById('toile') as HTMLCanvasElement
const couche = document.getElementById('interface') as HTMLDivElement
const chargement = document.getElementById('chargement') as HTMLDivElement
const jauge = document.getElementById('jauge') as HTMLDivElement
const texteChargement = document.getElementById('chargement-texte') as HTMLDivElement

function etape(pct: number, texte: string): void {
  jauge.style.width = `${pct}%`
  texteChargement.textContent = texte
}

/**
 * Identifiant unique.
 *
 * `crypto.randomUUID` n'existe QUE dans un contexte securise: en HTTPS ou sur
 * localhost. Comme le jeu se joue aussi depuis un telephone en HTTP sur le
 * reseau local, on ne peut pas s'y fier. En revanche
 * `crypto.getRandomValues` est toujours disponible.
 */
function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const o = new Uint8Array(16)
  crypto.getRandomValues(o)
  o[6] = ((o[6] as number) & 0x0f) | 0x40
  o[8] = ((o[8] as number) & 0x3f) | 0x80
  const hex = [...o].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function jeton(): string {
  let t = lireLocal(CLE_TOKEN)
  if (!t) {
    t = uuid()
    ecrireLocal(CLE_TOKEN, t)
    journal.info('identite', 'nouveau jeton cree')
  }
  return t
}

interface ReponseMonde {
  graine: number
  tempsJeu: number
  batiments: Batiment[]
  vehicules: Vehicule[]
  ressourcesEpuisees: { ressourceId: number; pv: number; repousseA: number }[]
  /** Animaux apprivoises, adoptes ou installes dans un enclos. */
  animaux: AnimalSauve[]
  /** Jardins cueillis et leur echeance de repousse, en temps de jeu. */
  jardins: JardinCueilli[]
}

async function chargerMonde(token: string): Promise<ReponseMonde> {
  try {
    // Le jeton fait deux choses: il ouvre la porte (le serveur exige un compte
    // hors mode test) et il permet au serveur de rendre a CHACUN ses propres
    // jetons de proprietaire intacts, masques pour ceux des autres.
    const r = await fetch('/api/monde', { headers: { 'x-jeu-token': token } })
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    return (await r.json()) as ReponseMonde
  } catch (e) {
    // Le jeu doit rester jouable meme si le serveur est indisponible: on
    // repart de la graine par defaut, sans persistance.
    journal.warn('monde', `chargement impossible, monde par defaut: ${String(e)}`)
    return {
      graine: WORLD_SEED,
      tempsJeu: TEMPS_DEPART,
      batiments: [],
      vehicules: [],
      ressourcesEpuisees: [],
      animaux: [],
      jardins: [],
    }
  }
}

async function chargerJoueur(token: string): Promise<EtatSauve | null> {
  try {
    const r = await fetch(`/api/joueur/${encodeURIComponent(token)}`, { headers: { 'x-jeu-token': token } })
    if (r.status === 404) return null
    if (r.status === 401) {
      // Le jeton local ne designe plus un compte (compte supprime, base
      // remplacee): continuer donnerait une partie ou plus rien ne se sauve.
      // On oublie le choix et on repasse par l'ecran de connexion.
      journal.warn('joueur', 'jeton refuse par le serveur: retour a la connexion')
      effacerLocal(CLE_CHOIX)
      effacerLocal(CLE_COMPTE)
      // La clef des tests aussi, sinon un invite refuse rechargerait en boucle.
      effacerLocal(CLE_INVITE)
      // La page repart de zero; ce qui suit ce `return` n'aura pas le temps
      // de se voir.
      location.reload()
      return null
    }
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    const s = (await r.json()) as EtatSauve
    return { ...s, apparence: validerApparence(s.apparence) }
  } catch (e) {
    journal.warn('joueur', `sauvegarde illisible: ${String(e)}`)
    return null
  }
}

/**
 * Identite du joueur pour cette partie.
 *
 * Deux cas: on s'est deja connecte sur ce navigateur (on reprend sans rien
 * demander), ou l'ecran de connexion s'ouvre et il faut un compte — il n'y a
 * plus de troisieme voie. Un ancien invite (choix fait, mais pas de compte)
 * retombe donc sur l'ecran de connexion; seule la clef des tests (`CLE_INVITE`)
 * rouvre encore le jeu sans compte.
 */
async function identifier(): Promise<{ token: string; compte: string | null }> {
  const dejaChoisi = lireLocal(CLE_CHOIX) === '1'
  const tokenLocal = lireLocal(CLE_TOKEN)
  const compteLocal = lireLocal(CLE_COMPTE)
  if (dejaChoisi && compteLocal && tokenLocal) {
    return { token: tokenLocal, compte: compteLocal }
  }
  if (lireLocal(CLE_INVITE) === '1') {
    // La passerelle des tests: jeton local anonyme, pas de salon. Voir le
    // commentaire de CLE_INVITE.
    ecrireLocal(CLE_CHOIX, '1')
    return { token: jeton(), compte: null }
  }

  chargement.classList.add('chargement--parti')
  const ecran = new Connexion(couche)
  const r = await ecran.ouvrir(compteLocal ?? '')
  chargement.classList.remove('chargement--parti')

  ecrireLocal(CLE_TOKEN, r.token)
  ecrireLocal(CLE_COMPTE, r.compte)
  ecrireLocal(CLE_CHOIX, '1')
  return { token: r.token, compte: r.compte }
}

async function demarrer(): Promise<void> {
  const { token, compte } = await identifier()
  await demarrerJournal(lireLocal(CLE_NOM) ?? compte ?? 'anonyme', token)
  journal.info('boot', 'jeu v0.1.0', { compte: compte ?? 'invite' })

  etape(5, 'Connexion au monde…')
  const [monde, sauve] = await Promise.all([chargerMonde(token), chargerJoueur(token)])
  journal.info('monde', `graine ${monde.graine}, ${monde.batiments.length} batiments`, {
    tempsJeu: monde.tempsJeu,
    epuisees: monde.ressourcesEpuisees.length,
  })

  let sauvegarde: EtatSauve
  if (sauve) {
    sauvegarde = sauve
    journal.info('joueur', `bienvenue ${sauve.nom}`, { pos: sauve.pos, besoins: sauve.besoins })
  } else {
    // Premiere partie: le createur de bonhomme, avant tout le reste.
    etape(10, 'Crée ton bonhomme…')
    chargement.classList.add('chargement--parti')
    const createur = new Creation(couche)
    const alea = new Alea(Date.now() & 0x7fffffff)
    const propose = apparenceAleatoire(alea)
    const nomPropose = compte ? compte.charAt(0).toUpperCase() + compte.slice(1) : ''
    const r = await createur.ouvrir(propose, nomPropose, false)
    createur.detruire()
    const nom = r?.nom?.trim() || 'Bonhomme'
    const apparence = r?.apparence ?? apparenceParDefaut()
    ecrireLocal(CLE_NOM, nom)
    // Rouvrir le journal maintenant que le nom est connu: le fichier de
    // session porte le nom du joueur.
    await demarrerJournal(nom, token)
    journal.info('creation', `bonhomme cree: ${nom}`, apparence)
    sauvegarde = {
      token,
      nom,
      apparence,
      inventaire: {},
      besoins: besoinsNeufs(),
      pos: { x: WORLD_SIZE / 2, z: WORLD_SIZE / 2 },
      tempsJeu: monde.tempsJeu,
    }
    chargement.classList.remove('chargement--parti')
  }

  etape(12, 'Préparation du monde…')
  const jeu = await Jeu.creer({
    toile,
    couche,
    compte,
    graine: monde.graine,
    tempsJeu: monde.tempsJeu || TEMPS_DEPART,
    sauvegarde,
    batiments: monde.batiments,
    vehicules: monde.vehicules ?? [],
    ressourcesEpuisees: monde.ressourcesEpuisees,
    animaux: monde.animaux ?? [],
    jardins: monde.jardins ?? [],
    progression: etape,
  })

  // Persister le personnage sans attendre la sauvegarde periodique: sinon,
  // fermer l'onglet dans les secondes qui suivent la creation le perdait.
  if (!sauve) await jeu.sauverMaintenant()

  chargement.classList.add('chargement--parti')
  jeu.demarrer()
  journal.info('boot', 'partie lancee', jeu.statistiques())

  // Le contexte audio ne peut demarrer qu'apres un geste: on saisit le premier.
  const amorcerSon = (): void => {
    void audio.init()
    removeEventListener('pointerdown', amorcerSon)
    removeEventListener('keydown', amorcerSon)
  }
  addEventListener('pointerdown', amorcerSon)
  addEventListener('keydown', amorcerSon)

  jeu.onDeconnexion = () => {
    journal.input('connexion', 'deconnexion demandee')
    jeu.sauverAvantFermeture()
    effacerLocal(CLE_CHOIX)
    effacerLocal(CLE_NOM)
    effacerLocal(CLE_INVITE)
    effacerLocal('jeu.admin')
    // On garde le jeton: le compte recevra de toute facon le sien a la
    // prochaine connexion.
    location.reload()
  }

  addEventListener('pagehide', () => {
    jeu.sauverAvantFermeture()
    jeu.arreter()
    arreterJournal('pagehide')
  })

  // Passerelle pour les tests automatises et le debogage a la console.
  // Surtout PAS `window.jeu`: le navigateur y expose deja <div id="jeu">, donc
  // un test qui attend `window.jeu` croirait la partie prete des la 1re image.
  ;(window as unknown as { __jeu: Jeu }).__jeu = jeu
}

demarrer().catch((e) => {
  journal.error('boot', `echec du demarrage: ${e instanceof Error ? e.message : String(e)}`, {
    stack: e instanceof Error ? e.stack : undefined,
  })
  texteChargement.textContent = `Échec du démarrage : ${e instanceof Error ? e.message : String(e)}`
  texteChargement.style.color = '#ef5f5f'
})
